import { useMemo } from "react";
import type { Layout, PlotData } from "plotly.js";
import { Plot } from "../lib/Plot";
import { plotlyBaseLayout, buildPlotlyConfig } from "../lib/plotlyTheme";
import { useIsMobile } from "./../lib/useMediaQuery";

interface PlotCardProps {
  title: string;
  subtitle?: string;
  data: Partial<PlotData>[];
  layout?: Partial<Layout>;
  height?: number;
  /** Accent bar colour on the top-left notch; null hides it. */
  accent?: string | null;
  /**
   * Stable identifier that controls Plotly's preserved UI state. As long
   * as this string doesn't change between renders the user's zoom/pan
   * survives data refreshes. Change it when the *axis* meaning changes
   * (e.g. switching x from time to chainage).
   */
  uirevision?: string;
}

/**
 * Mobile cap. Phones are wide enough for a 320 px tall plot to feel
 * cramped after axis ticks and legend; clamp anything above 280 to keep
 * the plot from dominating the viewport. Desktop is unchanged.
 */
const MOBILE_MAX_HEIGHT = 280;

export function PlotCard({
  title,
  subtitle,
  data,
  layout,
  height = 320,
  accent = "#c8102e",
  uirevision = "default",
}: PlotCardProps) {
  const isMobile = useIsMobile();
  const effectiveHeight = isMobile ? Math.min(height, MOBILE_MAX_HEIGHT) : height;
  // Per-instance Plotly config so the PNG export can bake in the card's
  // title and snapshot the live width/height (preserves aspect on
  // download). Memoised on title so the modebar isn't re-built every
  // render.
  const config = useMemo(() => buildPlotlyConfig({ title }), [title]);
  // Title + subtitle render as HTML in the card header, NOT as Plotly's
  // SVG title. Plotly's title positioning (y=0.98 in container coords)
  // routinely clips multi-line headings on shorter charts; rendering in
  // HTML makes that class of bug impossible.
  // Top-level shallow spread is fine for most fields, but xaxis/yaxis
  // need a per-key merge — otherwise per-plot settings (title, range,
  // …) silently clobber the base axis styling (gridcolor, tickfont,
  // tickformatstops). Without this merge, the time-of-day tickformat
  // stops in plotlyBaseLayout never reach a plot that sets `xaxis`.
  const mergedLayout: Partial<Layout> = {
    ...plotlyBaseLayout,
    ...layout,
    xaxis: { ...plotlyBaseLayout.xaxis, ...(layout?.xaxis ?? {}) },
    yaxis: { ...plotlyBaseLayout.yaxis, ...(layout?.yaxis ?? {}) },
    paper_bgcolor: "#ffffff",
    plot_bgcolor: "#ffffff",
    height: effectiveHeight,
    title: undefined,
    margin: {
      l: (layout?.margin?.l as number | undefined) ?? 56,
      r: (layout?.margin?.r as number | undefined) ?? 16,
      // No Plotly title means we only need a small top margin for axis
      // ticks; keep it tight so the chart fills the card.
      t: (layout?.margin?.t as number | undefined) ?? 16,
      b: (layout?.margin?.b as number | undefined) ?? 36,
    },
    uirevision,
  };

  return (
    // data-plot-title mirrors the live heading for the PNG-export
    // button: Plotly can keep a stale modebar closure across config
    // updates, so the click handler reads the current title from this
    // attribute at click time instead of trusting what it captured.
    <div className="card relative overflow-hidden" data-plot-title={title}>
      {accent && (
        <div
          className="absolute top-0 left-0 h-[2px] w-10 rounded-tl"
          style={{ background: accent }}
        />
      )}
      <header className="px-5 pt-4 pb-2">
        <h3 className="text-[14px] font-semibold text-text leading-tight">{title}</h3>
        {subtitle && (
          <p className="text-[11.5px] text-muted mt-1 leading-snug">{subtitle}</p>
        )}
      </header>
      <div className="px-2 pb-2">
        <Plot
          data={data}
          layout={mergedLayout}
          config={config}
          useResizeHandler
          style={{ width: "100%", height: effectiveHeight }}
        />
      </div>
    </div>
  );
}
